import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BadgeCheck, Calendar, Clock, MapPin } from 'lucide-react';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { PriceOnRequest } from '../components/PriceOnRequest';
import { apiFetch } from '../utils/operations';

export default function PartnerProductDetailPage() {
  const { id } = useParams();
  const { t } = useLang();
  const [product, setProduct] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    apiFetch(`/products/${id}`).then(setProduct).catch(() => setMissing(true));
  }, [id]);

  useSEO(product ? `${product.name} | Eco Collection` : 'Produto de Parceiro | Eco Collection', product?.description?.slice(0, 150) || 'Produto submetido por parceiro local e aprovado pela Eco Collection.');
  if (missing) return <Navigate to="/experiences" replace />;
  if (!product) return <div className="min-h-screen bg-eco-cream pt-40 text-center text-eco-ink/50">{t('loading')}</div>;

  const planUrl = `/plan-my-trip?destination=${encodeURIComponent(product.island)}&notes=${encodeURIComponent(`Interesse no produto do parceiro: ${product.name} (${product.partner_name})`)}`;
  return (
    <div data-testid={`partner-product-page-${product.id}`}>
      <PageHeader kicker="Rede Local aprovada" title={product.name} sub={product.description} img={product.images?.[0] ? `${process.env.REACT_APP_BACKEND_URL}${product.images[0]}` : undefined} />
      <section className="bg-eco-cream py-14 md:py-20">
        <div className="mx-auto max-w-5xl px-5 md:px-8">
          <Link to="/experiences" data-testid="partner-product-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-eco-ink/55 hover:text-eco-gold"><ArrowLeft size={15} /> {t('back')}</Link>
          <div className="mt-8 grid gap-6 lg:grid-cols-[1.3fr_1fr]">
            <Reveal>
              <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
                {product.images?.[0] ? <img src={`${process.env.REACT_APP_BACKEND_URL}${product.images[0]}`} alt={product.name} className="h-96 w-full object-cover" /> : <div className="grid h-96 place-items-center bg-eco-green font-serif text-7xl text-eco-gold">{product.name.charAt(0)}</div>}
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <aside className="rounded-2xl border border-eco-ink/10 bg-white p-6 shadow-sm lg:sticky lg:top-24">
                <p className="eco-eyebrow">{product.type}</p>
                <div className="mt-5 space-y-4 text-sm text-eco-ink/65">
                  <p className="flex items-center gap-2"><MapPin size={15} className="text-eco-gold" /> {product.location || product.island}</p>
                  {product.duration && <p className="flex items-center gap-2"><Clock size={15} className="text-eco-gold" /> {product.duration}</p>}
                  <p className="flex items-center gap-2"><BadgeCheck size={15} className="text-eco-green" /> {product.partner_name}</p>
                </div>
                <div className="mt-6 border-t border-eco-ink/10 pt-5">
                  {product.price_public_eur !== null && product.price_public_eur !== undefined && ['fixed_price', 'from_price'].includes(product.pricing_mode) ? (
                    <div data-testid="partner-product-detail-price" className="eco-price-approved">
                      <p className="eco-price-label">{product.pricing_mode === 'from_price' ? t('pm.from') : t('pm.fixed')}</p>
                      <p className="eco-price-value text-2xl">{product.price_label}</p>
                    </div>
                  ) : (
                    <PriceOnRequest testid="partner-product-detail-price" />
                  )}
                </div>
                <Link to={planUrl} data-testid="partner-product-plan" className="eco-btn-primary mt-5 w-full">{t('cta.availability')} <ArrowRight size={15} /></Link>
              </aside>
            </Reveal>
          </div>
          {product.availability?.length > 0 && (
            <Reveal className="mt-8 rounded-3xl border border-eco-ink/10 bg-white p-7" data-testid="partner-product-availability">
              <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-eco-ink"><Calendar size={18} className="text-eco-gold" /> {t('ppd.availnote')}</h2>
              <div className="mt-5 flex flex-wrap gap-2">{product.availability.map((s) => <span key={s.date} className="rounded-full bg-eco-cream px-4 py-2 text-xs font-semibold text-eco-ink/65">{s.date} · {s.status}{s.capacity !== null && s.capacity !== undefined ? ` · ${s.capacity}` : ''}</span>)}</div>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
